import { useState, useEffect, useCallback } from 'react';
import { Radio, Wifi, WifiOff, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { api } from '../../api/client';

interface Dispositivo {
  id: string;
  nome: string;
  codigo: string;
  tipo: string;
  status: string;
  localInstalacao: string | null;
  ultimoHeartbeatEm: string | null;
  observacoes: string | null;
  criadoEm: string;
  escola: {
    id: string;
    nome: string;
    municipio: { id: string; nome: string } | null;
  } | null;
}

const STATUS_COLORS: Record<string, { color: string; bg: string; icon: any }> = {
  ONLINE: { color: '#10b981', bg: '#ecfdf5', icon: Wifi },
  OFFLINE: { color: '#ef4444', bg: '#fef2f2', icon: WifiOff },
  FALHA: { color: '#f59e0b', bg: '#fffbeb', icon: AlertTriangle },
  MANUTENCAO: { color: '#8b5cf6', bg: '#f5f3ff', icon: AlertTriangle },
};

export function MasterDispositivosPage() {
  const [items, setItems] = useState<Dispositivo[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const limite = 20;

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), limite: String(limite) });
    if (statusFilter) params.set('status', statusFilter);

    api.get(`/master/dashboard/rfid/dispositivos?${params}`)
      .then(r => {
        const data = r.data;
        setItems(data.items || []);
        setTotal(data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [page, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages = Math.ceil(total / limite);

  const thStyle: React.CSSProperties = {
    padding: '10px 14px', fontSize: '0.75rem', fontWeight: 600,
    color: 'var(--text-muted)', textTransform: 'uppercase',
    letterSpacing: '0.04em', borderBottom: '1px solid var(--border-color)',
    textAlign: 'left',
  };
  const tdStyle: React.CSSProperties = {
    padding: '12px 14px', fontSize: '0.85rem', color: 'var(--text-main)',
    borderBottom: '1px solid var(--border-color, #f1f5f9)',
  };

  return (
    <div style={{ padding: '24px 28px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Radio size={22} style={{ color: '#6366f1' }} />
          <h1 style={{ fontSize: '1.3rem', fontWeight: 700, margin: 0, color: 'var(--text-main)' }}>
            Dispositivos RFID
          </h1>
          <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', background: 'var(--bg-card)', padding: '2px 10px', borderRadius: 20 }}>
            {total} total
          </span>
        </div>

        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          style={{
            padding: '6px 14px', borderRadius: 8, border: '1px solid var(--border-color)',
            background: 'var(--bg-card)', color: 'var(--text-main)', fontSize: '0.85rem',
            height: 34,
          }}
        >
          <option value="">Todos os status</option>
          <option value="ONLINE">Online</option>
          <option value="OFFLINE">Offline</option>
          <option value="FALHA">Falha</option>
          <option value="MANUTENCAO">Manutencao</option>
        </select>
      </div>

      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border-color)',
        borderRadius: 12, overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg-sidebar, #f8fafc)' }}>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Nome</th>
              <th style={thStyle}>Codigo</th>
              <th style={thStyle}>Tipo</th>
              <th style={thStyle}>Local</th>
              <th style={thStyle}>Escola</th>
              <th style={thStyle}>Municipio</th>
              <th style={thStyle}>Ultimo Heartbeat</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-muted)' }}>Nenhum dispositivo encontrado</td></tr>
            ) : items.map(d => {
              const sc = STATUS_COLORS[d.status] || STATUS_COLORS.OFFLINE;
              const Icon = sc.icon;
              return (
                <tr key={d.id} style={{ transition: 'background 0.15s' }} onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover, #f8fafc)')} onMouseLeave={e => (e.currentTarget.style.background = '')}>
                  <td style={tdStyle}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '3px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600,
                      background: sc.bg, color: sc.color,
                    }}>
                      <Icon size={12} />
                      {d.status}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{d.nome}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.8rem' }}>{d.codigo}</td>
                  <td style={tdStyle}>{d.tipo}</td>
                  <td style={tdStyle}>{d.localInstalacao || '-'}</td>
                  <td style={tdStyle}>{d.escola?.nome || '-'}</td>
                  <td style={tdStyle}>{d.escola?.municipio?.nome || '-'}</td>
                  <td style={{ ...tdStyle, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    {d.ultimoHeartbeatEm ? new Date(d.ultimoHeartbeatEm).toLocaleString('pt-BR') : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Paginacao */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            style={{
              padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border-color)',
              background: 'var(--bg-card)', cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1,
            }}
          >
            <ChevronLeft size={16} />
          </button>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Pagina {page} de {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            style={{
              padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border-color)',
              background: 'var(--bg-card)', cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.5 : 1,
            }}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
